import { useEffect, useRef, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { ERROR_TEXTS, errorLang, looksLikeNetworkDrop } from "@/lib/error-texts";
import { routePageLanguage, usePublishedPageLanguage } from "@/lib/page-language";
import { DEFAULT_META, OG_LOCALE, metaLang, socialMeta, type MetaLang } from "@/lib/page-meta";
import { analyticsHeadScripts, trackPageView } from "@/lib/analytics";
import { captureUtm } from "@/lib/utm";
import { noTranslatePath } from "@/lib/no-translate";
import { BRAND_FONT_LINKS, BRAND_ICON_LINKS, THEME_COLOR, pathHasOwnFonts } from "@/lib/brand-head";

// 404 на языке адреса (16.10.2026). Корневой head адреса не видит: при
// глобальном 404 у корня pathname «/». Поэтому на 404 корень свой <title> не
// отдаёт (isNotFound ниже; свежий match корня — его matches в head устаревшие), а
// страница ставит <title> сама — React 19 поднимает
// его в <head>.
const NOT_FOUND_TEXT: Record<MetaLang, { title: string; h2: string; p: string; home: string; href: string }> = {
  ru: { title: "Страница не найдена — Yolutap", h2: "Страница не найдена", p: "Такой страницы нет или она переехала.", home: "На главную", href: "/ru" },
  az: { title: "Səhifə tapılmadı — Yolutap", h2: "Səhifə tapılmadı", p: "Belə səhifə yoxdur və ya başqa ünvana köçüb.", home: "Ana səhifəyə", href: "/az" },
  en: { title: "Page not found — Yolutap", h2: "Page not found", p: "The page you're looking for doesn't exist or has been moved.", home: "Go home", href: "/en" },
};

const isNotFound = (matches: { status?: string; globalNotFound?: boolean }[]) =>
  matches.some((m) => m.status === "notFound" || m.globalNotFound);

function NotFoundComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const t = NOT_FOUND_TEXT[metaLang(pathname)];
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <title>{t.title}</title>
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t.h2}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.p}</p>
        <div className="mt-6">
          <Link
            to={t.href}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t.home}
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Граница ошибки роутера — КЛИЕНТСКАЯ. Именно её видел класс 7 сентября:
 * серверная граница ответила бы пятисоткой, а Cloudflare за то окно показывает
 * 1886 успехов и ноль ошибок.
 *
 * Она перестала утверждать, где сломалось. Обрыв связи и отказ сервера — две
 * разные новости для ребёнка: первая значит «нажми ещё раз», вторая — «дело не
 * в тебе». Раньше обе печатались фразой «на нашей стороне», и в наблюдавшемся
 * случае это была неправда.
 */
function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  // Считаем ОДИН раз при монтировании: navigator.onLine меняется, пока экран
  // висит, и текст не должен переобуваться под читающим его ребёнком.
  const [network] = useState(() => looksLikeNetworkDrop(error));
  const [lang] = useState(errorLang);
  const t = ERROR_TEXTS[lang];

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {network ? t.titleNetwork : t.titleServer}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {network ? t.network : t.server}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t.retry}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t.home}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Дефолтные тексты — на языке адреса (src/lib/page-meta.ts). Раньше они
  // были азербайджанско-английскими на любой странице, и маршрут без своих
  // twitter-тегов отдавал чужой язык.
  head: ({ matches, match }) => {
    const leaf = matches[matches.length - 1];
    const searchLang = matches
      .map((m) => (m.search as { lang?: unknown } | undefined)?.lang)
      .find(Boolean);
    const lang = metaLang(leaf?.pathname ?? "/", searchLang);
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        // На 404 title ставит сама страница 404 (NotFoundComponent): тут адреса не видно.
        ...socialMeta(DEFAULT_META[lang]).filter((m) => !(isNotFound([match, ...matches]) && "title" in m)),
        { name: "author", content: "Yolutap" },
        { name: "theme-color", content: THEME_COLOR },
        // Тест и отчёт — без автоперевода Chrome (src/lib/no-translate.ts).
        ...(noTranslatePath(leaf?.pathname ?? "/") ? [{ name: "google", content: "notranslate" }] : []),
        { property: "og:type", content: "website" },
        { property: "og:locale", content: OG_LOCALE[lang] },
        { name: "twitter:card", content: "summary" },
        {
          property: "og:image",
          content: "https://yolutap.com/photos/teen_boy_hero.jpg",
        },
        {
          name: "twitter:image",
          content: "https://yolutap.com/photos/teen_boy_hero.jpg",
        },
      ],
      // Шрифты и иконки — один раз здесь, не постранично (src/lib/brand-head.ts).
      links: [
        ...BRAND_ICON_LINKS,
        ...(pathHasOwnFonts(leaf?.pathname ?? "/") ? [] : BRAND_FONT_LINKS),
        { rel: "stylesheet", href: appCss },
      ],
      scripts: [
        {
          // Cloudflare Turnstile loader. Used by:
          //   - <TurnstileWidget /> on /start (React)
          //   - inline widgets on the static methodology*.html forms
          //     (reviews + contact). Those pages serve as raw HTML and
          //     load this script themselves anyway — the loader here
          //     just makes sure the React surfaces have it too.
          // `defer` + render="explicit" keeps the load off the critical
          // path and lets each widget mount when its component does.
          src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
          async: true,
          defer: true,
        },
        // Marketing analytics loaders (GA4 gtag + Meta Pixel). SSR-injected
        // into <head> on every router page so page_view fires site-wide.
        // Emits nothing when VITE_GA4_MEASUREMENT_ID / VITE_META_PIXEL_ID are
        // unset — the whole feature is a no-op until those build-time env vars
        // are configured in Cloudflare Workers Builds. See src/lib/analytics.
        ...analyticsHeadScripts(),
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/**
 * `<html lang>`: объявленный страницей язык, иначе язык маршрута
 * (routePageLanguage — ?lang, pageLang из загрузчика, путь, «ru»).
 * Read by `RootShell` via `useRouterState` so the attribute reflects the
 * actual served content per request (SSR-correct for a11y and SEO).
 */
function RootShell({ children }: { children: React.ReactNode }) {
  // ОБЪЯВЛЕННЫЙ ЯЗЫК СИЛЬНЕЕ МАРШРУТА. Страница знает про себя больше: у
  // /redeem язык выбирает человек, в кабинете — профиль, и в маршруте нет ни
  // того, ни другого. Не объявил никто — работает вывод из маршрута.
  const published = usePublishedPageLanguage();
  const routeLang = useRouterState({
    select: (state) => routePageLanguage(state.matches, state.location.pathname),
  });
  const noTranslate = useRouterState({ select: (state) => noTranslatePath(state.location.pathname) });

  return (
    <html lang={published ?? routeLang} translate={noTranslate ? "no" : undefined}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Fire `page_view` on the first client paint and on every subsequent SPA
 * navigation. GA4 is configured with `send_page_view:false` and the Meta
 * pixel snippet omits its auto-PageView, so this effect is the single source
 * of page views — one per navigation, including the initial load. No-ops on
 * the server and when no analytics id is configured (trackPageView guards).
 */
function useAnalyticsPageViews() {
  const href = useRouterState({ select: (s) => s.location.href });
  const lastHref = useRef<string | null>(null);

  useEffect(() => {
    // Skip no-op re-renders and the React 19 strict-mode double invoke.
    if (lastHref.current === href) return;
    lastHref.current = href;
    // utm_* из адреса — запомнить до анкеты (src/lib/utm.ts); и в GA, и без него.
    captureUtm(window.location.search);
    trackPageView(href);
  }, [href]);
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useAnalyticsPageViews();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
